/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

// @ts-ignore
import * as semver from 'semver';

import { createObservableDataAction } from '@msdyn365-commerce/action-internal';
import { IAction, IActionContext } from '@msdyn365-commerce/core-internal';
import {
    getByIdAsync,
    getRelatedProductsAsync,
    searchByCriteriaAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { ProductSearchCriteria, ProductSearchResult, SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
// @ts-ignore
import { getProductImageUrls, orderProductSearchResults } from '@msdyn365-commerce/runtime-internal/dist/lib/utils/product-list-utils';
import { ProductListInput } from '@msdyn365-commerce/runtime-internal/dist/types';

const MIN_RETAIL_PROXY_VERSION_FOR_PRODUCT_SEARCH_RESULTS = '9.16.8';

const actionInputError = (missingType: string) => {
    throw new Error(`Input ${missingType} is missing and required to run GetProductsByRelationship action`);
};

// tslint:disable-next-line: max-func-body-length
export const getProductsByRelationshipIdList = async (input: ProductListInput, context: IActionContext): Promise<ProductSearchResult[]> => {
    let productId;
    let relationshipId;
    // Grab the product ID from the list meta data or context
    if (input.listMetadata.productIds && input.listMetadata.productIds.length > 0) {
        const productResponse: SimpleProduct | SimpleProduct[] = await getByIdAsync(
            { callerContext: context, queryResultSettings: { Paging: { Top: input.listMetadata.pageSize || 10 } } },
            input.listMetadata.productIds[0],
            input.channelId
        );
        const baseProduct: SimpleProduct = Array.isArray(productResponse) ? productResponse[0] : productResponse;
        productId = input.listMetadata.productIds[0];
        if (baseProduct.hasOwnProperty('MasterProductId') && baseProduct.MasterProductId && baseProduct.MasterProductId > 0) {
            productId = baseProduct.MasterProductId;
        }
    } else if (context.requestContext.urlTokens && context.requestContext.urlTokens.itemId) {
        const productResponse = await getByIdAsync(
            { callerContext: context, queryResultSettings: { Paging: { Top: input.listMetadata.pageSize || 10 } } },
            +context.requestContext.urlTokens.itemId,
            input.channelId
        );
        const baseProduct = Array.isArray(productResponse) ? productResponse[0] : productResponse;
        productId = +context.requestContext.urlTokens.itemId;
        if (baseProduct.hasOwnProperty('MasterProductId') && baseProduct.MasterProductId > 0) {
            productId = baseProduct.MasterProductId;
        }
    } else {
        actionInputError('productId');
    }
    // The relationship ID must be provided with the list meta data
    if (input.listMetadata.relationshipId) {
        relationshipId = +input.listMetadata.relationshipId;
    } else {
        actionInputError('relationshipId');
    }
    context.telemetry.debug(`Product Id input to productByRelationship action: ${productId}`);
    context.telemetry.debug(`Relationship Id input to productByRelationship action: ${relationshipId}`);

    return getRelatedProductsAsync(
        { callerContext: context, queryResultSettings: { Paging: { Top: input.listMetadata.pageSize || 10 } } },
        // @ts-ignore
        productId,
        input.channelId,
        input.catalogId,
        // @ts-ignore
        relationshipId
    ).then(
            /*VSI Customization - START
            This data-action is customized as per Microsoft's guidance to use searchByCriteriaAsync call to get
            product attributes for related to products product-collection list
            */
                async (products: ProductSearchResult[]) => {
                    context.telemetry.debug('Products returned by getRelatedProducts action', products);
                    if (products.length) {
                        if (
                            semver.gte(
                                semver.coerce(context.requestContext.apiSettings.retailServerProxyVersion) || '',
                                MIN_RETAIL_PROXY_VERSION_FOR_PRODUCT_SEARCH_RESULTS
                            )
                        ) {
                            const itemIds: number[] = products.map(value => value.RecordId);
                            const productSearchCriteria: ProductSearchCriteria = {
                                Ids: itemIds,
                                Context: {
                                    ChannelId: +context.requestContext.apiSettings.channelId,
                                    CatalogId: +context.requestContext.apiSettings.catalogId
                                },
                                IncludeAttributes: true,
                                SearchCondition: '*'
                            };
                            try {
                                let productSearchResults = await searchByCriteriaAsync(
                                    { callerContext: context, queryResultSettings: { Paging: { Top: input.listMetadata.pageSize || 10 } } },
                                    productSearchCriteria
                                );
                                // If the ProductSearchResult API finds the products then populate the product image urls and return
                                // otherwise if the API does not exist or does not return products proceed to the legacy flows for legacy/backward compatibility reasons
                                context.telemetry.debug('Product search results returned', JSON.stringify(productSearchResults));
                                if (productSearchResults.length > 0) {
                                    getProductImageUrls(productSearchResults, context.requestContext.apiSettings);
                                    productSearchResults = orderProductSearchResults(itemIds, productSearchResults);
                                    return productSearchResults;
                                }
                            } catch (e) {
                                // In case of an error fall back to legacy flow
                                context.telemetry.error(`Error while getting productSearchResult: ${e}`);
                            }
                        }
                        return getProductImageUrls(products, context.requestContext.apiSettings);
                    }
                }
            )
            // @ts-ignore
            .catch(error => {
                context.telemetry.error(`Error running productByRecommendation action: ${error}`);
                throw new Error(error);
            });
    /*VSI Customization - END*/
};

export const getProductsByRelationshipIdListAction = createObservableDataAction({
    action: <IAction<ProductSearchResult[]>>getProductsByRelationshipIdList,
    id: '@msdyn365-commerce/products-by-relationship-id'
});
